/**
 * Data Hub — Risk & Opportunity Scores tab.
 *
 * Ported from `components/pipeline/ro-scores-console.tsx` (1,088 LOC), the
 * largest surface in the console: RCS/OCS composite tiles with band labels, the
 * two 7-row dimension tables, and the Trend Analysis block with line tabs
 * (ROQ Quadrant, Trend, Top movers) including the Risk–Opportunity Quadrant scatter.
 *
 * Semantics carried over deliberately (easy to get wrong):
 *  - RCS is "risk health": 0 = critical risk, 100 = minimal risk. Low is bad.
 *  - Colours run low=red → high=green on BOTH scales.
 *  - Quadrants: high risk is rcs < 50, high opportunity is ocs >= 50.
 */
import { useMemo, useState } from "react";

import { ArrowUpDown, LineChart, ScatterChart } from "lucide-react";

import TabsComponent from "../../Components/Tab";
import Table from "../../Components/Table";
import { TabCard, TabError, TabLoading } from "../components/TabState";
import type {
  RoAccountPoint,
  RoDimension,
  RoQuadrant,
  RoScores,
} from "../data/types";
import { useActiveDataset } from "../hooks/useActiveDataset";
import { useAdapterData } from "../hooks/useAdapterData";
import { useDataHubAdapter } from "../hooks/useDataHubAdapter";

const SUB_TABS = [
  {
    id: "quadrant",
    label: "ROQ Quadrant",
    icon: <ScatterChart size={16} strokeWidth={2} />,
  },
  {
    id: "trend",
    label: "Trend",
    icon: <LineChart size={16} strokeWidth={2} />,
  },
  {
    id: "top-movers",
    label: "Top movers",
    icon: <ArrowUpDown size={16} strokeWidth={2} />,
  },
];

const QUADRANTS: {
  key: RoQuadrant;
  label: string;
  sub: string;
  dot: string;
}[] = [
  {
    key: "Q1",
    label: "Q1 · Invest & Grow",
    sub: "High Opp, Low Risk",
    dot: "bg-green-500",
  },
  {
    key: "Q2",
    label: "Q2 · Stabilise then Grow",
    sub: "High Opp, High Risk",
    dot: "bg-amber-500",
  },
  {
    key: "Q3",
    label: "Q3 · Develop & Nurture",
    sub: "Low Opp, Low Risk",
    dot: "bg-blue-500",
  },
  {
    key: "Q4",
    label: "Q4 · Defend & Remediate",
    sub: "Low Opp, High Risk",
    dot: "bg-red-500",
  },
];

/** Low = red, high = green — on both scales. */
function scoreTone(v: number) {
  if (v >= 70) return "bg-green-500 text-white";
  if (v >= 50) return "bg-green-600 text-white";
  if (v >= 35) return "bg-amber-500 text-white";
  return "bg-red-500 text-white";
}

function badgeTone(v: number) {
  if (v >= 70) return "bg-green-100 text-green-900";
  if (v >= 50) return "bg-green-50 text-green-800";
  if (v >= 35) return "bg-amber-100 text-amber-900";
  return "bg-red-100 text-red-900";
}

function DimensionTable({
  title,
  rows,
}: {
  title: string;
  rows: RoDimension[];
}) {
  const columns = useMemo(
    () => [
      {
        key: "key",
        title: "Dim",
        width: "56px",
        render: (value: string) => (
          <span className="font-mono text-xs text-gray-500">{value}</span>
        ),
      },
      {
        key: "label",
        title: "Label",
        render: (_: unknown, d: RoDimension) => (
          <>
            <span className="text-gray-900">{d.label}</span>
            {d.optional && (
              <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
                optional
              </span>
            )}
          </>
        ),
      },
      {
        key: "share",
        title: "Share",
        textalign: "left" as const,
        render: (value: number) => (
          <span className="tabular-nums text-gray-600">
            {value.toFixed(1)}%
          </span>
        ),
      },
      {
        key: "n",
        title: "n",
        textalign: "left" as const,
        render: (value: number) => (
          <span className="tabular-nums text-gray-600">{value}</span>
        ),
      },
      {
        key: "score",
        title: "Score",
        textalign: "left" as const,
        render: (value: number) => (
          <span
            className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-semibold ${badgeTone(value)}`}
          >
            {value.toFixed(1)}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div>
      <div className="border-b border-gray-200 px-4 py-3">
        <p className="m-0 text-sm font-semibold text-gray-900">{title}</p>
      </div>
      <Table
        className="[&_th]:normal-case [&_th]:tracking-normal"
        columns={columns}
        data={rows}
        rowKey={record => record.key}
      />
    </div>
  );
}

function QuadrantScatter({ accounts }: { accounts: RoAccountPoint[] }) {
  const w = 900;
  const h = 380;
  const pad = 46;
  const x = (rcs: number) => pad + (rcs / 100) * (w - pad * 2);
  const y = (ocs: number) => h - pad - (ocs / 100) * (h - pad * 2);

  return (
    <div>
      <p className="mb-2 text-xs text-gray-600">
        Risk–Opportunity Quadrant (ROQ) · {accounts.length} accounts
      </p>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="w-full"
        role="img"
        aria-label="Risk-Opportunity quadrant"
      >
        {[0, 25, 50, 75, 100].map(g => (
          <g key={`gx-${g}`}>
            <line
              x1={x(g)}
              x2={x(g)}
              y1={pad}
              y2={h - pad}
              stroke="#f3f4f6"
              strokeWidth={1}
            />
            <text
              x={x(g)}
              y={h - pad + 14}
              fontSize={9}
              fill="#9ca3af"
              textAnchor="middle"
            >
              {g}
            </text>
          </g>
        ))}
        {[0, 25, 50, 75, 100].map(g => (
          <g key={`gy-${g}`}>
            <line
              x1={pad}
              x2={w - pad}
              y1={y(g)}
              y2={y(g)}
              stroke="#f3f4f6"
              strokeWidth={1}
            />
            <text
              x={pad - 8}
              y={y(g) + 3}
              fontSize={9}
              fill="#9ca3af"
              textAnchor="end"
            >
              {g}
            </text>
          </g>
        ))}

        {/* Quadrant dividers: risk at rcs=50, opportunity at ocs=50 */}
        <line
          x1={x(50)}
          x2={x(50)}
          y1={pad}
          y2={h - pad}
          stroke="#d1d5db"
          strokeDasharray="4 4"
        />
        <line
          x1={pad}
          x2={w - pad}
          y1={y(50)}
          y2={y(50)}
          stroke="#d1d5db"
          strokeDasharray="4 4"
        />

        {accounts.map(a => (
          <circle
            key={a.account_id}
            cx={x(a.rcs)}
            cy={y(a.ocs)}
            r={5}
            className="fill-green-500/70 stroke-green-700"
            strokeWidth={1}
          >
            <title>{`${a.account_name} — RCS ${a.rcs.toFixed(2)}, OCS ${a.ocs.toFixed(2)} (${a.quadrant})`}</title>
          </circle>
        ))}

        <text
          x={w / 2}
          y={h - 6}
          fontSize={10}
          fill="#6b7280"
          textAnchor="middle"
        >
          RCS — Risk Health (higher = healthier →)
        </text>
        <text
          x={12}
          y={h / 2}
          fontSize={10}
          fill="#6b7280"
          textAnchor="middle"
          transform={`rotate(-90 12 ${h / 2})`}
        >
          OCS (opp ↑)
        </text>
      </svg>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {QUADRANTS.map(q => (
          <div
            key={q.key}
            className="rounded-lg border border-gray-200 px-3 py-2"
          >
            <p className="m-0 flex items-center gap-2 text-xs font-semibold text-gray-900">
              <span className={`inline-block h-2 w-2 rounded-full ${q.dot}`} />
              {q.label}
            </p>
            <p className="m-0 text-[11px] text-gray-500">
              {q.sub} · {accounts.filter(a => a.quadrant === q.key).length} acct
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrendAnalysis({ data }: { data: RoScores }) {
  const [sub, setSub] = useState("quadrant");

  const trendColumns = useMemo(
    () => [
      { key: "period", title: "Period" },
      {
        key: "rcs",
        title: "RCS",
        textalign: "left" as const,
        render: (value: number) => (
          <span className="tabular-nums">{value.toFixed(2)}</span>
        ),
      },
      {
        key: "ocs",
        title: "OCS",
        textalign: "left" as const,
        render: (value: number) => (
          <span className="tabular-nums">{value.toFixed(2)}</span>
        ),
      },
    ],
    []
  );

  const topMoverColumns = useMemo(
    () => [
      { key: "account_name", title: "Account" },
      {
        key: "metric",
        title: "Metric",
        render: (value: string) => (
          <span className="text-gray-600">{value}</span>
        ),
      },
      {
        key: "delta",
        title: "Δ",
        textalign: "left" as const,
        render: (value: number) => (
          <span
            className={`tabular-nums ${value >= 0 ? "text-green-700" : "text-red-700"}`}
          >
            {value >= 0 ? "+" : ""}
            {value.toFixed(1)}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <TabCard title="Trend Analysis (month) — Portfolio">
      <div className="mb-4 overflow-x-auto">
        <TabsComponent
          tabs={SUB_TABS}
          activeTab={sub}
          onTabChange={setSub}
          variant="tab-underline"
          className="mb-0"
        />
      </div>

      {sub === "quadrant" && <QuadrantScatter accounts={data.accounts} />}

      {sub === "trend" && (
        <Table
          className="[&_th]:normal-case [&_th]:tracking-normal"
          columns={trendColumns}
          data={data.trend}
          rowKey={record => record.period}
        />
      )}

      {sub === "top-movers" && (
        <Table
          className="[&_th]:normal-case [&_th]:tracking-normal"
          columns={topMoverColumns}
          data={data.top_movers}
          rowKey={(record, index) => `${record.account_name}-${index}`}
        />
      )}
    </TabCard>
  );
}

export function RoScoresTab() {
  const [dataset] = useActiveDataset();
  const adapter = useDataHubAdapter();
  const { data, loading, error } = useAdapterData(
    () => adapter.getRoScores(),
    [dataset]
  );

  if (loading) return <TabLoading label="Loading risk & opportunity scores…" />;
  if (error) return <TabError error={error} />;
  if (!data) return null;

  return (
    <div className="space-y-0">
      <TabCard
        title="Risk & Opportunity Scores"
        subtitle="View-only snapshot of persisted RCS and OCS. Recomputes run as part of Data Hub Phase 2."
      >
        <div className="mb-4 flex flex-wrap gap-2 text-[11px] text-gray-500">
          <span className="rounded bg-gray-100 px-2 py-0.5">
            All scores: 0 = bad · 100 = good
          </span>
          <span className="rounded bg-gray-100 px-2 py-0.5">
            RCS = Risk Health · high = low risk
          </span>
          <span className="rounded bg-gray-100 px-2 py-0.5">
            OCS · high = strong opportunity
          </span>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-gray-50/40 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1">
              <p className="m-0 text-sm font-semibold text-gray-900">
                Risk Composite Score (RCS)
              </p>
              <p className="m-0 mt-1 text-xs leading-relaxed text-gray-500">
                Risk Health · 0 = critical (high risk) · 100 = minimal risk
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <div
                className={`flex h-14 min-w-[4.5rem] items-center justify-center rounded-lg px-4 text-3xl font-bold tabular-nums ${scoreTone(data.rcs)}`}
              >
                {data.rcs.toFixed(2)}
              </div>
              <span className="max-w-[9rem] text-sm font-medium leading-snug text-gray-700">
                {data.rcs_band?.trim() || "—"}
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-gray-50/40 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1">
              <p className="m-0 text-sm font-semibold text-gray-900">
                Opportunity Composite Score (OCS)
              </p>
              <p className="m-0 mt-1 text-xs leading-relaxed text-gray-500">
                0 = no opportunity · 100 = maximum expansion
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <div
                className={`flex h-14 min-w-[4.5rem] items-center justify-center rounded-lg px-4 text-3xl font-bold tabular-nums ${scoreTone(data.ocs)}`}
              >
                {data.ocs.toFixed(2)}
              </div>
              <span className="max-w-[9rem] text-sm font-medium leading-snug text-gray-700">
                {data.ocs_band?.trim() || "—"}
              </span>
            </div>
          </div>
        </div>
      </TabCard>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DimensionTable title="Risk dimensions" rows={data.risk_dimensions} />
        <DimensionTable
          title="Opportunity dimensions"
          rows={data.opportunity_dimensions}
        />
      </div>

      <TrendAnalysis data={data} />
    </div>
  );
}

export default RoScoresTab;
